import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import Modal from 'components/Modal';
import styled from 'styled-components';
import { useSignals } from '@preact/signals-react/runtime';
import appointmentActions from 'features/appointment/services/actions';
import { useAppDispatch } from 'store/hooks';
import { Form } from 'antd';
import SelectStatusConfig from './SelectStatusConfig';
import SelectColorByConfigStatus from './SelectColorByConfigStatus';
import TypeOfBg from './TypeOfBg';
import StaffSidebar from './StaffSidebar';
import ColorTable from './ColorTable';
import appointmentSelectors from 'features/appointment/services/selectors';
import appointmentApis from 'features/appointment/services/apis';
import { IStaffItemData } from 'services/shop/types/staff';
import toast from 'utils/toast';
import { get } from 'lodash';

export type IStatusBarBgColorModalRef = {
  open: () => void;
};

type FormType = {
  type: 'status' | 'staff';
  statusConfig: any;
  staffs: IStaffItemData[];
  staffObj: IStaffItemData;
};

const StatusBarBgColorModal = forwardRef<IStatusBarBgColorModalRef, {}>((props, ref) => {
  useSignals();
  const [visible, setVisible] = useState(false);
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const staffListStore = appointmentSelectors.getListStaffAvailableBooking();

  const handleOpen = () => {
    setVisible(true);
  };

  useImperativeHandle(ref, () => ({
    open: handleOpen,
  }));

  const handleCancel = () => {
    setVisible(false);
  };

  const handleSubmit = async (values: FormType) => {
    try {
      if (values.type === 'staff') {
        const body = values.staffs.map((staff: IStaffItemData) => ({
          staffId: staff.id,
          color: staff.color || '',
        }));
        const res = await appointmentApis.updateStaffsColor(body);
        if (res?.data?.data) {
          dispatch(appointmentActions.initData());
          handleCancel();
        } else {
          toast.error('Update staff color failed');
        }
      }
    } catch (error) {
      const msg = get(error, 'response.data.message');
      toast.error(msg || 'Update staff color failed');
    }
  };

  useEffect(() => {
    form.setFieldsValue({ staffs: staffListStore });
    form.setFieldValue('staffObj', staffListStore[0]);
  }, [staffListStore]);

  const type = Form.useWatch('type', form);

  return (
    <Modal
      modalTitle='color customization'
      width={'80vw'}
      visible={visible}
      onClose={handleCancel}
      hiddenOkButton={type === 'status'}
      onSubmit={form.submit}
      noneBodyStyle
      closable={false}
      footerPadding={0.5}
      destroyOnClose
      v2
    >
      <Form
        form={form}
        initialValues={{ statusConfig: 'MULTI', type: 'status', staffs: [] }}
        onFinish={handleSubmit}
      >
        <Form.Item name='type' noStyle>
          <TypeOfBg />
        </Form.Item>

        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => {
            const type = getFieldValue('type');
            return type === 'status' ? (
              <Container>
                <Left>
                  <Form.Item name='statusConfig' noStyle>
                    <SelectStatusConfig />
                  </Form.Item>
                </Left>
                <Form.Item name='statusConfig' noStyle>
                  <SelectColorByConfigStatus />
                </Form.Item>
              </Container>
            ) : (
              <Container>
                <Form.Item name='staffs' noStyle>
                  <StaffSidebar />
                </Form.Item>
                <Form.Item name='staffObj' noStyle>
                  <ColorTable />
                </Form.Item>
              </Container>
            );
          }}
        </Form.Item>
      </Form>
    </Modal>
  );
});

StatusBarBgColorModal.displayName = 'StatusBarBgColorModal';
export default StatusBarBgColorModal;

const Container = styled.div`
  display: flex;
  gap: 24px;
`;

const Left = styled.div`
  display: flex;
  width: 289px;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  background: #fff;
  box-shadow: 4px 0 8px 0 rgba(0, 0, 0, 0.15);
`;
